import {
  computed,
  effect,
  inject,
  Injectable,
  linkedSignal,
  resource,
  Signal,
  signal,
  WritableSignal,
} from '@angular/core';
import { InventoryDetailStrategy } from './inventory-detail.strategy';
import { MdcInventoryService } from '../../../services/inventory.service';
import { askFn, messageFn, modalFn, translator } from '@softline/ui-core';
import {
  navigateFn,
  queryParam,
  queryParamAsNumber,
  requestFn,
  routeParamAsNumber,
} from '@softline/application';
import { SOFTLINE_CONFIG_MDC_INVENTORY } from '../../../mdc-inventory.tokens';
import { InventoryConfigService } from '../../../services/inventory-config.service';
import { InventoryQuantityControl } from '../../../components/inventory-quantity-control/inventory-quantity-control';
import { MdcInventoryDetail } from '../../../types/inventory-detail';
import { ApiDataAccess, base64Decode, isDefinedNotEmpty } from '@softline/core';
import { MdcPalletItem } from '@softapps/mdc/core';
import { StorageLocationModalComponent } from '../../../modals/storage-location-modal/storage-location-modal.component';
import { MdcInventoryItemRepository } from '../../../repositories/inventory-item.repository';

@Injectable()
export class InventoryDetailEditStrategy extends InventoryDetailStrategy {
  private readonly service = inject(MdcInventoryService);
  private readonly repository = inject(MdcInventoryItemRepository);

  private readonly dataAccess = inject(ApiDataAccess);

  private readonly modal = modalFn();
  private readonly t = translator();
  private readonly messageBar = messageFn();
  private readonly navigate = navigateFn();
  private readonly request = requestFn();
  private readonly ask = askFn();

  private readonly config = inject(SOFTLINE_CONFIG_MDC_INVENTORY);
  protected readonly remoteConfig = inject(InventoryConfigService);

  override readonly palletData = linkedSignal(() => {
    const item = this.inventoryItem();
    if (!item || !item?.pallets || !this.itemId()()) return null;
    const pallet = item.pallets.find(
      (o) => !!o.products.find((p) => p.inventoryItemId === this.itemId()())
    );

    if (!pallet) return null;

    return {
      pallet,
    };
  });

  protected readonly inventoryItemResource = resource({
    params: () => {
      const inventory = this.service.inventory();
      const productId = this.productId()();
      if (!inventory || !productId) return undefined;

      return {
        id: inventory.id,
        productId,
        itemId: this.itemId()(),
      };
    },
    loader: async ({ params }) => {
      const data = await this.repository.items(params.id, params.productId);
      return data.find((o) => o.id === params.itemId);
    },
  });

  override readonly inventoryItem = computed(() => {
    if (this.inventoryItemResource.hasValue())
      return this.inventoryItemResource.value() ?? null;

    return null;
  });

  readonly quantityControlComponent = signal(
    signal<InventoryQuantityControl | null | undefined>(null)
  );

  readonly productDetails: WritableSignal<Signal<MdcInventoryDetail | null>> = signal(
    signal(null)
  );

  readonly qty = linkedSignal({
    source: () => {
      return {
        id: this.productId()(),
        quantity: this.palletData()
          ? (this.inventoryItem()
              ?.pallets.find(
                (o) => !!o.products.find((p) => p.inventoryItemId === this.itemId()())
              )
              ?.products.find((p) => p.inventoryItemId === this.itemId()())
              ?.remainingAmount ?? 0)
          : (this.inventoryItem()?.productData?.amount ?? null),
      };
    },
    computation: ({ quantity }) => {
      console.log(
        this.inventoryItem()
          ?.pallets.find(
            (o) => !!o.products.find((p) => p.inventoryItemId === this.itemId()())
          )
          ?.products.find((p) => p.inventoryItemId === this.itemId()())?.remainingAmount
      );

      console.log('qty: ', quantity);
      return quantity ?? 1;
    },
  });

  readonly storageLocationId = computed<number | null>(() => {
    return this.storageLocation()?.id ?? null;
  });

  protected readonly fiscalPeriod = signal<{
    jahr: number;
    vondat: string;
    bisdat: string;
    id: number;
  } | null>(null);

  protected readonly fiscalPeriodId = computed(() => {
    return this.fiscalPeriod()?.id;
  });

  private readonly storageLocationScanModalDisplayed = signal(false);

  protected readonly sumUpVisible = computed(() => {
    const sumUp = this.remoteConfig.value()?.addierenSichtbar ?? this.config.sumUp;
    if (typeof sumUp === 'boolean') return sumUp;
    else if (typeof sumUp === 'object') return sumUp.visible ?? false;
    else return false;
  });

  protected readonly sumUpEditable = computed(() => {
    const sumUp = this.remoteConfig.value()?.addierenErlaubt ?? this.config.sumUp;
    if (typeof sumUp === 'boolean') return true;
    else if (typeof sumUp === 'object') return sumUp.editable ?? false;
    else return false;
  });

  constructor() {
    super();
    effect(() => {
      console.log('this.itemId()', this.itemId()());
      console.log('this.palletData()', this.palletData());
      console.log('this.inventoryItem()', this.inventoryItem());
    });
  }

  async delete(): Promise<boolean> {
    const inventory = this.service.inventory();
    const itemId = this.itemId()();
    if (!itemId || !inventory?.id) return false;

    const askResult = await this.ask({
      dismiss: true,
      showCancel: false,
      title: 'Bewegung löschen',
      question: 'Wollen Sie die Inventurbewegung wirklich löschen?',
    });

    if (!askResult || askResult !== 'YES') return false;

    return (
      (await this.request({
        request: async () => {
          try {
            await this.dataAccess.delete({
              path: 'inventory-service/inventory-service/{{id}}/items/{{itemId}}',
              pathParams: {
                id: inventory.id,
                itemId,
              },
            });
            await this.service.loadById(inventory.id); // reload to reflect updates in list
            return true;
          } catch {
            return false;
          }
        },
        options: {
          type: 'blocking',
        },
      })) ?? false
    );
  }

  override async confirm(
    params: {
      inventoryId: number;
      productId: number;
      stockId: number;
      unitId: number;
      sumUnitId: number | null;
      sumAmount: number | null;
      palletItemId?: number | null;
      pallet: any;
      quantity: number;
      productDetails: MdcInventoryDetail;
    },
    saveOnly = false
  ): Promise<void> {
    const inventoryId = params.inventoryId;
    const productId = params.productId;
    const stockId = params.stockId;
    const inventory = this.service.inventory();
    if (!inventoryId || !productId || !stockId || !inventory) return;

    const payload = {
      amount: params.quantity ?? 1,
      storageLocationId: null, // barcode (scan) // Test 647
      unitId: params.unitId ?? 0, // TODO
      bbd: params.pallet?.bbd ?? null,
      lot: params.pallet?.lot ?? null,
      weight: null,
      weightUnitId: null,
      sumUnitId: params?.sumUnitId,
      sumAmount: params?.sumAmount,
    };

    if (params.productDetails?.storageLocationRequired && !this.storageLocationId()) {
      this.storageLocationScanModalDisplayed.set(true);

      const result = await this.modal({
        title: 'Lagerplatz auswahl',
        subtitle:
          'Es muss ein Lagerplatz ausgewählt werden um diese Aktion durchzuführen',
        component: StorageLocationModalComponent,
        data: {
          inventory,
          storageLocation: this.palletData()
            ? !this.palletData()?.pallet?.storageLocation?.id
              ? undefined
              : {
                  id: this.palletData()?.pallet?.storageLocation?.id,
                  lagerplatz: this.palletData()?.pallet?.storageLocation?.name,
                  barcode: this.palletData()?.pallet?.storageLocation?.description,
                }
            : null,
        },
      });

      this.storageLocationScanModalDisplayed.set(false);

      if (!result || result === 'DISMISSED') {
        this.messageBar.warning(
          this.t(
            'Um den Artikel übernehmen zu können muss ein Lagerplatz ausgewählt oder gescannt werden!'
          )
        );
        return;
      }

      this.storageLocation.set({
        id: result.id,
        name: result?.lagerplatz,
        description: '',
      });
    }

    if (this.unitEntryId()) {
      payload['unitEntryId'] = this.unitEntryId();
    }

    if (this.sumUpVisible() && this.sumUpEditable()) {
      payload['sumUp'] = this.sumUp();
    }

    if (this.storageLocationId()) {
      payload['storageLocationId'] = this.storageLocationId() as any;
    }

    if (this.fiscalPeriodId()) {
      payload['fiscalPeriodId'] = this.fiscalPeriodId();
    }

    if (this.productDetails()?.()?.manProductByQuery?.idManProductDat) {
      payload['manproductDatId'] =
        this.productDetails()?.()?.manProductByQuery?.idManProductDat;
    }

    if (this.salePriceRequired()) {
      if ((this.salePriceIncl() ?? 0) <= 0) {
        this.messageBar.info(signal('Der VK-Preis muss erfasst werden.'));
        return;
      }

      payload['salePrice'] = this.salePriceIncl();
    }

    // hinweis abweichende menge
    if (
      this.productDetails()?.()?.stock?.quantity !== this.qty() &&
      this.remoteConfig.value()?.hinweisAbweichendeMenge &&
      !this.productDetails()?.()?.manProductByQuery?.id
    ) {
      const questionResult = await this.ask({
        title: 'Menge abweichend zu Lagerstand',
        question:
          'Die eingegebene Menge unterscheidet sich vom Lagerstand - trotzdem erfassen?',
        showCancel: true,
        dismiss: true,
      });

      if (questionResult !== 'YES') return;
    }

    await this.request({
      request: async () => {
        await this.dataAccess.patch(
          {
            path: 'inventory-service/inventory-service/{{id}}/items/{{itemId}}',
            pathParams: {
              id: inventoryId,
              itemId: this.itemId()(),
            },
          },
          payload
        );
        await this.service.loadById(inventoryId); // reload to reflect updates in list
      },
      options: {
        type: 'blocking',
      },
    });

    await this.navigate(['./../../../../'], { relativeTo: this.route() });

    /*
    console.log('palletdata', this.palletData());

    if (params.productDetails?.storageLocationRequired && !this.storageLocationId()) {
      this.storageLocationScanModalDisplayed.set(true);

      const result = await this.modal({
        title: 'Lagerplatz auswahl',
        subtitle:
          'Es muss ein Lagerplatz ausgewählt werden um diese Aktion durchzuführen',
        component: StorageLocationModalComponent,
        data: {
          inventory,
          storageLocation: this.palletData()
            ? {
                id: this.palletData()?.pallet?.storageLocation.id,
                lagerplatz: this.palletData()?.pallet?.storageLocation.name,
                barcode: this.palletData()?.pallet?.storageLocation.description,
              }
            : null,
        },
      });

      this.storageLocationScanModalDisplayed.set(false);

      if (!result || result === 'DISMISSED') {
        this.messageBar.warning(
          this.t(
            'Um den Artikel übernehmen zu können muss ein Lagerplatz ausgewählt oder gescannt werden!'
          )
        );
        return;
      }

      this.storageLocation.set({
        id: result.id,
        name: result?.lagerplatz,
        description: '',
      });
    }

    if (this.unitEntryId()) {
      payload['unitEntryId'] = this.unitEntryId();
    }

    if (this.sumUpVisible() && this.sumUpEditable()) {
      payload['sumUp'] = this.sumUp();
    }

    if (this.storageLocationId()) {
      payload['storageLocationId'] = this.storageLocationId() as any;
    }

    if (this.fiscalPeriodId()) {
      payload['fiscalPeriodId'] = this.fiscalPeriodId();
    }

    if (this.productDetails()?.()?.manProductByQuery?.idManProductDat) {
      payload['manproductDatId'] =
        this.productDetails()?.()?.manProductByQuery?.idManProductDat;
    }

    if (this.salePriceRequired()) {
      if ((this.salePriceIncl() ?? 0) <= 0) {
        this.messageBar.info(signal('Der VK-Preis muss erfasst werden.'));
        return;
      }

      payload['salePrice'] = this.salePriceIncl();
    }

    // hinweis abweichende menge
    if (
      this.productDetails()?.()?.stock?.quantity !== this.qty() &&
      this.remoteConfig.value()?.hinweisAbweichendeMenge &&
      !this.productDetails()?.()?.manProductByQuery?.id
    ) {
      const questionResult = await this.ask({
        title: 'Menge abweichend zu Lagerstand',
        question:
          'Die eingegebene Menge unterscheidet sich vom Lagerstand - trotzdem erfassen?',
        showCancel: true,
        dismiss: true,
      });

      if (questionResult !== 'YES') return;
    }

    await this.request({
      request: () => this.service.createOrUpdateItem(inventoryId, payload),
      options: {
        type: 'blocking',
      },
    });

    if (saveOnly) return;

    if (this.palletData()) {
      const palletItems = this.palletData()?.pallet?.palettItems as MdcPalletItem[];
      const index = palletItems.findIndex(
        (palletItem) => palletItem?.id === this.palletItemId()
      );

      const nextIndex = Math.min(index + 1, palletItems.length - 1);
      const nextItem =
        index === nextIndex
          ? null
          : nextIndex <= palletItems.length - 1
            ? palletItems[nextIndex]
            : null;

      if (nextItem) {
        await this.navigate(['./../', nextItem.product.id], {
          relativeTo: this.route(),
          queryParamsHandling: 'merge',
          queryParams: {
            palletItemId: nextItem.id,
          },
        });
      } else {
        await this.navigate(['./../../'], { relativeTo: this.route() });
      }
    } else {
      await this.navigate(['./../../'], { relativeTo: this.route() });
    }
    */
  }
}
